import * as THREE from 'three'
import type { VisualParams } from '../../types'
import type { SharedUniforms } from '../AudioUniforms'
import { PRIMITIVE_TEXTURE_GLSL } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, POINT_SPRITE_GLSL, ZONE_GLSL_COMMON, ZONE_GLSL_FRAG } from '../shaderChunks'

/**
 * Vortex: large points that spiral from the back and rush toward the camera
 * (swirl + travel in Z). Designed for maximum sense of motion; pairs very well
 * with high Trails.
 */
export class Vortex extends BasePrimitive {
  object3D: THREE.Points
  private material: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    this.material = new THREE.ShaderMaterial({
      uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        attribute float aProgress;  // offset 0..1 along the vortex
        attribute float aArm;       // base angle 0..2PI
        attribute float aRadSeed;   // radius variation 0..1
        uniform float uTime, uBass, uTreble, uLevel, uBeat, uReactivity, uBassPunch, uPixelRatio, uPointScale;
        uniform vec3 uSeed;
        uniform float uSeedPulse;
        uniform sampler2D uFreq;
        varying float vP;
        varying float vMag;
        varying float vSeed;
        varying vec3 vDir;
        ${ZONE_GLSL_COMMON}
        void main() {
          // When zone reaction is on, the vortex is frozen — only the zones react.
          float gate = 1.0 - uZoneEnabled;
          // Advance toward the camera (faster with energy).
          float speed = 0.20 * (1.0 + uLevel * 1.2);
          float p = fract(aProgress + uTime * speed * gate);

          float mag = texture2D(uFreq, vec2(fract(aArm / 6.2831853), 0.5)).r;

          // Swirl: several turns depending on depth + global rotation.
          float twist = 6.2831853 * 2.5;
          float angle = aArm + p * twist + uTime * 0.8 * gate;

          // Radius: narrow at the back, opening up as it approaches (funnel effect).
          float radius = mix(0.25, 4.4, pow(p, 0.7)) * (0.8 + aRadSeed * 0.5);
          radius += (mag * 0.9 * uReactivity + uBass * uBassPunch * 0.5) * gate;
          // Seed-driven beat punch (suppressed when zone reaction is on).
          float lobe = sin(aArm * 2.0 + uSeed.x) * cos(p * 12.566 + uSeed.y);
          radius += lobe * uSeedPulse * uReactivity * 0.8;
          // Zone reaction: shapes ride (arm angle, progress) and warp the funnel.
          vec3 zdir = zoneDirFromUV(vec2(fract(aArm / 6.2831853), p));
          vDir = zdir;
          radius += zoneDisp(zdir) * 1.4;

          float z = mix(-38.0, 7.5, p);   // from the back until it passes the camera (z=7)
          vec3 pos = vec3(cos(angle) * radius, sin(angle) * radius, z);

          vec4 mv = modelViewMatrix * vec4(pos, 1.0);
          gl_Position = projectionMatrix * mv;
          vP = p; vMag = mag; vSeed = aRadSeed;

          float px = (5.0 + mag * 9.0 + uBeat * 5.0) * (12.0 / -mv.z);
          gl_PointSize = clamp(px, 2.0, 60.0) * uPixelRatio * uPointScale;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        ${PRIMITIVE_TEXTURE_GLSL}
        uniform float uHue, uTreble, uLevel;
        varying float vP;
        varying float vMag;
        varying float vSeed;
        varying vec3 vDir;
        ${hsv2rgb}
        ${POINT_SPRITE_GLSL}
        ${ZONE_GLSL_COMMON}
        ${ZONE_GLSL_FRAG}
        void main() {
          float core = pointMask();
          if (core < 0.01) discard;
          // Appears in the back and fades out as it passes by.
          float fade = smoothstep(0.0, 0.07, vP) * smoothstep(1.0, 0.88, vP);
          float hue = fract(uHue + vP * 0.3 + vMag * 0.15 + vSeed * 0.05 + uTreble * 0.1);
          vec3 col = hsv2rgb(vec3(hue, 0.8, 0.55 + vMag * 0.45 + vP * 0.2));
          float alpha = core * fade * (uPointSolid > 0.5 ? 1.0 : (0.4 + vMag * 0.6));
          vec4 zc = zoneColor(vDir);
          vec3 outc = mix(applyPrimitiveTexture(col * core), zc.rgb * core, zc.a);
          gl_FragColor = vec4(outc, max(alpha, zc.a * core * fade));
        }
      `,
    })
    this.object3D = new THREE.Points(new THREE.BufferGeometry(), this.material)
    this.object3D.frustumCulled = false
    this.rebuild({ density: 1 } as VisualParams)
  }

  update(params: VisualParams): void {
    const solid = params.pointSolid === true
    this.material.blending = solid ? THREE.NormalBlending : THREE.AdditiveBlending
    this.material.depthWrite = solid
  }

  rebuild(params: VisualParams): void {
    const count = Math.floor(14000 * (params.density ?? 1))
    const positions = new Float32Array(count * 3)
    const progress = new Float32Array(count)
    const arm = new Float32Array(count)
    const radSeed = new Float32Array(count)
    const arms = 6 // spiral arms

    for (let i = 0; i < count; i++) {
      positions[i * 3 + 0] = 0
      positions[i * 3 + 1] = 0
      positions[i * 3 + 2] = 0
      progress[i] = Math.random()
      // Distribute across arms so the spiral reads clearly, with some scatter.
      const armIndex = i % arms
      arm[i] = (armIndex / arms) * Math.PI * 2 + (Math.random() - 0.5) * 0.6
      radSeed[i] = Math.random()
    }

    const geo = this.object3D.geometry
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('aProgress', new THREE.BufferAttribute(progress, 1))
    geo.setAttribute('aArm', new THREE.BufferAttribute(arm, 1))
    geo.setAttribute('aRadSeed', new THREE.BufferAttribute(radSeed, 1))
    geo.computeBoundingSphere()
  }
}
